import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, X, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { E } from "@/lib/overridesContext";
import { useAuth } from "@/lib/auth";
import {
  appearanceIndex,
  appearanceName,
  deriveInnings,
  formatToss,
  pickLogo,
} from "@/lib/playhq";

/** Turn PlayHQ innings names ("FIRST_INNINGS", "SECOND_INNINGS", …) into
 *  friendly ordinals for the innings pill labels. */
function prettyInningsName(name) {
  if (!name) return "";
  const map = {
    FIRST_INNINGS: "1st Innings",
    SECOND_INNINGS: "2nd Innings",
    THIRD_INNINGS: "3rd Innings",
    FOURTH_INNINGS: "4th Innings",
  };
  return map[name] || name.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Full PlayHQ-style scorecard modal.
 *
 * Shows the toss result, an innings pill selector, and batting + bowling
 * tables + fall of wickets for each innings. A separate Line-up tab lists
 * the complete team appearances (roster).
 *
 * Data comes from `/api/playhq/games/{game_id}/summary` — cached in Mongo
 * so repeat opens are instant.
 */
export default function ScorecardDialog({ gameId, open, onClose }) {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inningsIdx, setInningsIdx] = useState(0);
  const { isAdmin } = useAuth();
  // Admin-only: canonical name → PlayHQ id map from the player registry.
  // Fetched once when the dialog opens so every row in every innings
  // pulls the same authoritative id we surface in the admin registry
  // tab, the career lookup, and the leaderboards.
  const [registryMap, setRegistryMap] = useState(null);

  useEffect(() => {
    if (!open || !gameId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setGame(null);
      setInningsIdx(0);
      try {
        const r = await api.get(`/playhq/games/${gameId}/summary`);
        if (!cancelled) setGame(r.data?.data || null);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't load scorecard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, gameId]);

  // Registry map — admin-only, fetched separately so a slow registry
  // response never blocks the scorecard from rendering.
  useEffect(() => {
    if (!open || !isAdmin) { setRegistryMap(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/playhq/admin/players/registry-map");
        if (!cancelled) setRegistryMap(r.data?.map || {});
      } catch { /* silent — falls back to appearance ids */ }
    })();
    return () => { cancelled = true; };
  }, [open, isAdmin]);

  // ---- Derived structures ----
  const appIndex = useMemo(() => appearanceIndex(game), [game]);
  const teamsById = useMemo(() => {
    const m = {};
    for (const t of (game?.teams) || []) m[t.id] = t;
    return m;
  }, [game]);
  const teamName = (id) => teamsById[id]?.name || "Unknown";

  const innings = useMemo(() => {
    if (!game) return [];
    return (game.periods || [])
      .sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
      .map((p) => deriveInnings(p, appIndex, registryMap))
      .filter(Boolean);
  }, [game, appIndex, registryMap]);

  const current = innings[inningsIdx];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden p-0 gap-0 flex flex-col"
        data-testid="playhq-scorecard-dialog"
      >
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="mt-2 text-sm">Loading scorecard…</span>
          </div>
        ) : !game ? (
          <div className="p-16 text-center text-muted-foreground text-sm">Scorecard unavailable.</div>
        ) : (
          <>
            {/* Header — teams + download + close */}
            <ScorecardHeader game={game} teamsById={teamsById} gameId={gameId} onClose={onClose} />

            {/* Tabs */}
            <Tabs defaultValue="scorecard" className="flex-1 min-h-0 flex flex-col">
              <div className="px-4 sm:px-6 pt-3 border-b border-border">
                <TabsList data-testid="playhq-scorecard-tabs">
                  <TabsTrigger value="scorecard" data-testid="playhq-tab-scorecard">Scorecard</TabsTrigger>
                  <TabsTrigger value="lineup" data-testid="playhq-tab-lineup">Line-up</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="scorecard" className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 mt-0">
                {/* Toss row */}
                {game.coinToss && (
                  <div
                    className="rounded-lg bg-[hsl(var(--secondary))] px-4 py-2.5 text-sm"
                    data-testid="playhq-toss"
                  >
                    <span className="font-semibold">Toss</span>
                    <span className="ml-3 text-foreground/80">{formatToss(game.coinToss, teamName)}</span>
                  </div>
                )}

                {/* Innings pills */}
                {innings.length > 1 && (
                  <div className="flex flex-wrap gap-2" data-testid="playhq-innings-pills">
                    {innings.map((inn, i) => (
                      <button
                        key={inn.id || `${inn.battingTeamId}-${inn.name}-${i}`}
                        type="button"
                        onClick={() => setInningsIdx(i)}
                        className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors border ${
                          i === inningsIdx
                            ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white"
                            : "border-border text-foreground/70 hover:border-foreground/40"
                        }`}
                        data-testid={`playhq-innings-pill-${i}`}
                      >
                        {teamName(inn.battingTeamId)} {prettyInningsName(inn.name)}
                      </button>
                    ))}
                  </div>
                )}

                {current ? (
                  <>
                    {/* Batting section */}
                    <SectionHeader>{teamName(current.battingTeamId)} Batting</SectionHeader>
                    <BattingTable innings={current} gameId={gameId} />
                    <TotalsRow innings={current} gameId={gameId} />

                    {/* Bowling section */}
                    <SectionHeader>{teamName(current.bowlingTeamId)} Bowling</SectionHeader>
                    <BowlingTable innings={current} gameId={gameId} />

                    {/* Fielding section */}
                    {current.fielders && current.fielders.length > 0 && (
                      <>
                        <SectionHeader>{teamName(current.bowlingTeamId)} Fielding</SectionHeader>
                        <FieldingTable innings={current} gameId={gameId} />
                      </>
                    )}

                    {/* Fall of wickets */}
                    {current.fallOfWickets.length > 0 && (
                      <>
                        <SectionHeader>Fall of Wickets</SectionHeader>
                        <FallOfWickets innings={current} gameId={gameId} />
                      </>
                    )}
                  </>
                ) : (
                  <div className="py-10 text-center text-sm text-muted-foreground">No innings data available.</div>
                )}
              </TabsContent>

              <TabsContent value="lineup" className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 mt-0">
                <LineupPanel game={game} teamsById={teamsById} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ScorecardHeader({ game, teamsById, gameId, onClose }) {
  const [t1, t2] = (game.teams || []);
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    if (!gameId || downloading) return;
    setDownloading(true);
    try {
      // We can't rely on <a download> to send our Authorization header,
      // so fetch the PDF via the api client and materialise it into a
      // click-triggered download.
      const res = await api.get(`/playhq/games/${gameId}/scorecard.pdf`, {
        responseType: "blob",
      });
      // Try to pick up the server-provided filename
      let filename = "scorecard.pdf";
      const disp = res.headers?.["content-disposition"];
      if (disp) {
        const m = /filename="?([^";]+)"?/i.exec(disp);
        if (m) filename = m[1];
      }
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Release memory after the browser had a chance to persist the file
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Scorecard PDF downloaded");
    } catch (err) {
      toast.error("Couldn't download scorecard PDF");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="border-b border-border px-4 sm:px-6 py-4 flex items-center gap-2 sm:gap-4">
      <TeamLine team={t1} teamsById={teamsById} align="left" />
      <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground shrink-0">vs</span>
      <TeamLine team={t2} teamsById={teamsById} align="right" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleDownload}
        aria-label="Download scorecard PDF"
        title="Download PDF"
        disabled={downloading || !gameId}
        className="shrink-0"
        data-testid="playhq-scorecard-download"
      >
        {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClose}
        aria-label="Close"
        className="shrink-0 -mr-2"
        data-testid="playhq-scorecard-close"
      >
        <X className="h-5 w-5" />
      </Button>
    </div>
  );
}

function TeamLine({ team, teamsById, align }) {
  const t = teamsById[team?.id] || team;
  const logo = pickLogo(t);
  const name = t?.name || "Team";
  return (
    <div className={`flex-1 min-w-0 flex items-center gap-2 sm:gap-3 ${align === "right" ? "justify-end flex-row-reverse" : ""}`}>
      {logo ? (
        <img src={logo} alt="" className="h-9 w-9 rounded-full object-cover shrink-0 bg-[hsl(var(--secondary))]" />
      ) : (
        <div className="h-9 w-9 rounded-full bg-[hsl(var(--secondary))] shrink-0" />
      )}
      <span className="text-sm sm:text-base font-semibold tracking-tight truncate">{name}</span>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div
      className="rounded-t-lg bg-[hsl(var(--accent))] text-white font-semibold tracking-tight px-4 py-2.5 mt-2 text-sm sm:text-base"
      data-testid="playhq-section-header"
    >
      {children}
    </div>
  );
}

function BattingTable({ innings, gameId }) {
  const { isAdmin } = useAuth();
  const inn = innings.inningsName || "innings";
  return (
    <div className="mobile-scroll-x rounded-b-lg border border-t-0 border-border">
      <table className="w-full text-sm" data-testid="playhq-batting-table">
        <thead className="bg-[hsl(var(--secondary))] text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          <tr>
            <th className="text-left py-2 px-3 font-medium">Batter</th>
            <th className="text-left py-2 px-3 font-medium">Dismissal</th>
            <th className="text-right py-2 px-3 font-medium">R</th>
            <th className="text-right py-2 px-3 font-medium">B</th>
            <th className="text-right py-2 px-3 font-medium">4s</th>
            <th className="text-right py-2 px-3 font-medium">6s</th>
            <th className="text-right py-2 px-3 font-medium">SR</th>
          </tr>
        </thead>
        <tbody>
          {innings.batters.map((b) => {
            const base = `scorecard.${gameId || "g"}.${inn}.bat.${b.appearanceId}`;
            return (
              <tr key={b.appearanceId} className="border-t border-border/60" data-testid={`playhq-batter-row-${b.appearanceId}`}>
                <td className="py-2 px-3 font-medium text-foreground">
                  <div>
                    <E path={`${base}.name`} defaultValue={b.name} />
                    {b.captain && <span className="ml-1 text-xs text-muted-foreground">(c)</span>}
                  </div>
                  {isAdmin && (
                    <div className="text-[10px] font-mono text-[hsl(var(--accent))]/80 mt-0.5" title="PlayHQ player id — click to edit (admin-only)">
                      PlayHQ · <E path={`${base}.playhqId`} defaultValue={b.playhqId || ""} emptyLabel="unset" />
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-foreground/75">
                  <E path={`${base}.dismissal`} defaultValue={b.dismissal || ""} emptyLabel="—" />
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold">
                  <E path={`${base}.runs`} defaultValue={String(b.runs ?? 0)} />
                  {b.status === "NOT_OUT" ? <span className="text-muted-foreground">*</span> : ""}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.balls`} defaultValue={String(b.balls ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.fours`} defaultValue={String(b.fours ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.sixes`} defaultValue={String(b.sixes ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.sr`} defaultValue={b.strikeRate != null ? Number(b.strikeRate).toFixed(2) : "0.00"} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TotalsRow({ innings, gameId }) {
  const t = innings.totals;
  const inn = innings.inningsName || "innings";
  const total = t.TOTAL_SCORE ?? 0;
  const wkts = t.TOTAL_OUTS ?? 0;
  const overs = t.TOTAL_OVERS;
  const extras = t.TOTAL_EXTRAS ?? 0;
  const extrasBreakdown = [
    t.EXTRA_BYES ? `b ${t.EXTRA_BYES}` : null,
    t.EXTRA_LEG_BYES ? `lb ${t.EXTRA_LEG_BYES}` : null,
    t.EXTRA_WIDES ? `w ${t.EXTRA_WIDES}` : null,
    t.EXTRA_NO_BALLS ? `nb ${t.EXTRA_NO_BALLS}` : null,
    t.EXTRA_PENALTY_RUNS ? `pen ${t.EXTRA_PENALTY_RUNS}` : null,
  ].filter(Boolean).join(", ");
  const base = `scorecard.${gameId || "g"}.${inn}.totals`;

  return (
    <div className="rounded-lg bg-[hsl(var(--secondary))]/60 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <div>
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mr-2">Total</span>
        <span className="font-mono tabular-nums font-semibold text-base" data-testid="playhq-innings-total">
          <E path={`${base}.score`} defaultValue={`${wkts}/${total}`} />
          {overs != null && (
            <span className="ml-1.5 text-muted-foreground font-normal">
              (<E path={`${base}.overs`} defaultValue={String(overs)} /> overs)
            </span>
          )}
        </span>
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="mr-2 font-semibold text-foreground/70">
          Extras <E path={`${base}.extras`} defaultValue={String(extras)} />
        </span>
        {extrasBreakdown && (
          <span>
            (<E path={`${base}.extras-breakdown`} defaultValue={extrasBreakdown} />)
          </span>
        )}
      </div>
    </div>
  );
}

function BowlingTable({ innings, gameId }) {
  const { isAdmin } = useAuth();
  const inn = innings.inningsName || "innings";
  if (innings.bowlers.length === 0) {
    return <div className="rounded-b-lg border border-t-0 border-border p-4 text-sm text-muted-foreground">No bowling data recorded.</div>;
  }
  return (
    <div className="mobile-scroll-x rounded-b-lg border border-t-0 border-border">
      <table className="w-full text-sm" data-testid="playhq-bowling-table">
        <thead className="bg-[hsl(var(--secondary))] text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          <tr>
            <th className="text-left py-2 px-3 font-medium">Bowler</th>
            <th className="text-right py-2 px-3 font-medium">O</th>
            <th className="text-right py-2 px-3 font-medium">M</th>
            <th className="text-right py-2 px-3 font-medium">R</th>
            <th className="text-right py-2 px-3 font-medium">W</th>
            <th className="text-right py-2 px-3 font-medium">Wd</th>
            <th className="text-right py-2 px-3 font-medium">Nb</th>
            <th className="text-right py-2 px-3 font-medium">Econ</th>
          </tr>
        </thead>
        <tbody>
          {innings.bowlers.map((b) => {
            const base = `scorecard.${gameId || "g"}.${inn}.bowl.${b.appearanceId}`;
            return (
              <tr key={b.appearanceId} className="border-t border-border/60" data-testid={`playhq-bowler-row-${b.appearanceId}`}>
                <td className="py-2 px-3 font-medium">
                  <div><E path={`${base}.name`} defaultValue={b.name} /></div>
                  {isAdmin && (
                    <div className="text-[10px] font-mono text-[hsl(var(--accent))]/80 mt-0.5" title="PlayHQ player id — click to edit (admin-only)">
                      PlayHQ · <E path={`${base}.playhqId`} defaultValue={b.playhqId || ""} emptyLabel="unset" />
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  <E path={`${base}.overs`} defaultValue={String(b.overs ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  <E path={`${base}.maidens`} defaultValue={String(b.maidens ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  <E path={`${base}.runs`} defaultValue={String(b.runs ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold">
                  <E path={`${base}.wickets`} defaultValue={String(b.wickets ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/70">
                  <E path={`${base}.wides`} defaultValue={String(b.wides ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/70">
                  <E path={`${base}.noBalls`} defaultValue={String(b.noBalls ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/70">
                  <E path={`${base}.econ`} defaultValue={b.economy != null ? Number(b.economy).toFixed(2) : "0.00"} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FallOfWickets({ innings }) {
  return (
    <div className="rounded-b-lg border border-t-0 border-border p-3 sm:p-4 flex flex-wrap gap-2" data-testid="playhq-fall-of-wickets">
      {innings.fallOfWickets.map((f) => (
        <span
          key={f.sequenceNo}
          className="rounded-md bg-[hsl(var(--secondary))] px-3 py-1.5 text-xs"
        >
          <span className="font-semibold">{f.sequenceNo}/{f.runs}</span>
          <span className="ml-2 text-muted-foreground">{f.name}</span>
        </span>
      ))}
    </div>
  );
}

// Fielding table — one row per contributor with catches / stumpings / run
// outs aggregated from the innings' shared dismissal records. Editable by
// admins like every other stats table via the `<E path>` wrapper.
function FieldingTable({ innings, gameId }) {
  const { isAdmin } = useAuth();
  const inn = innings.inningsName || innings.name || "innings";
  const rows = innings.fielders || [];
  if (rows.length === 0) {
    return <div className="rounded-b-lg border border-t-0 border-border p-4 text-sm text-muted-foreground">No fielding contributions recorded.</div>;
  }
  return (
    <div className="mobile-scroll-x rounded-b-lg border border-t-0 border-border">
      <table className="w-full text-sm" data-testid="playhq-fielding-table">
        <thead className="bg-[hsl(var(--secondary))] text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          <tr>
            <th className="text-left py-2 px-3 font-medium">Fielder</th>
            <th className="text-right py-2 px-3 font-medium" title="Catches">C</th>
            <th className="text-right py-2 px-3 font-medium" title="Stumpings">St</th>
            <th className="text-right py-2 px-3 font-medium" title="Run outs">RO</th>
            <th className="text-right py-2 px-3 font-medium" title="Dismissals total">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const base = `scorecard.${gameId || "g"}.${inn}.field.${f.appearanceId}`;
            const total = (f.catches || 0) + (f.stumpings || 0) + (f.runouts || 0);
            return (
              <tr key={f.appearanceId} className="border-t border-border/60" data-testid={`playhq-fielder-row-${f.appearanceId}`}>
                <td className="py-2 px-3 font-medium text-foreground">
                  <div><E path={`${base}.name`} defaultValue={f.name} /></div>
                  {isAdmin && (
                    <div className="text-[10px] font-mono text-[hsl(var(--accent))]/80 mt-0.5" title="PlayHQ player id — click to edit (admin-only)">
                      PlayHQ · <E path={`${base}.playhqId`} defaultValue={f.playhqId || ""} emptyLabel="unset" />
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold">
                  <E path={`${base}.catches`} defaultValue={String(f.catches ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.stumpings`} defaultValue={String(f.stumpings ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-foreground/75">
                  <E path={`${base}.runouts`} defaultValue={String(f.runouts ?? 0)} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold text-foreground/85">
                  {total}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LineupPanel({ game, teamsById }) {
  const groups = useMemo(() => {
    const map = {};
    for (const a of (game.appearances) || []) {
      const tid = a.teamId;
      if (!map[tid]) map[tid] = [];
      map[tid].push(a);
    }
    return Object.entries(map).map(([tid, players]) => ({
      team: teamsById[tid] || { name: "Team" },
      players: players
        .filter((p) => p.visible !== false)
        .sort((a, b) => (a.playerNumber ?? 999) - (b.playerNumber ?? 999)),
    }));
  }, [game, teamsById]);

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {groups.map((g) => (
        <div key={g.team.id || g.team.name} className="rounded-lg border border-border overflow-hidden">
          <div className="bg-[hsl(var(--accent))] text-white px-4 py-2.5 font-semibold text-sm">
            {g.team.name}
          </div>
          <ul className="divide-y divide-border/60">
            {g.players.map((p) => (
              <li key={p.id} className="flex items-baseline gap-2 px-4 py-2 text-sm">
                <span className="w-6 text-xs text-muted-foreground tabular-nums">
                  {p.playerNumber ?? ""}
                </span>
                <span className="flex-1 truncate">
                  {appearanceName(p)}
                  {p.captainRole && <span className="ml-1 text-xs text-muted-foreground">(c)</span>}
                  {p.isFillIn && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-600">Fill-in</span>}
                </span>
                {p.roleType && p.roleType !== "Player" && (
                  <span className="text-xs text-muted-foreground">{p.roleType}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
